<template>
  <div :class="componentClass.root">
    <PmDraggablePure
      type="changeDayWidth"
      :use-placeholder="false"
      :can-be-dropped="false"
      :use-mirror="false"
      constrain="x"
      :class="componentClass.element('dragAndDrop')"
      @drag-start="onDragStart"
      @drag-move="onDragMove"
      @drag-end="onDragEnd"
    >
      <PmDayWidthControllerPure :class="componentClass.element('controller')" />
    </PmDraggablePure>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useComponentClass } from '@thomasaull-shared/composables'
import PmDayWidthControllerPure from '@/components/persoplan/PmDayWidthController/PmDayWidthControllerPure.vue'
import PmDraggablePure from '@/components/utilities/PmDraggablePure.vue'

export interface Props {
  index?: number
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
})

export interface Emits {
  dragstart: []
  dragend: []
}

const emit = defineEmits<Emits>()

const componentClass = useComponentClass({ props })
const store = useStore()

const initialDayWidth = ref<number | undefined>(undefined)
const minDayWidth = ref<number | undefined>(undefined)
const maxDayWidth = ref<number | undefined>(undefined)
const intermediateDayWidth = ref<number | undefined>(undefined)
const isDragging = ref(false)

function onDragStart() {
  const variable = store.getters['cssVar/get']('dayWidth')
  initialDayWidth.value = variable.value
  minDayWidth.value = variable.min
  maxDayWidth.value = variable.max
  isDragging.value = true

  emit('dragstart')
}

function onDragMove({ delta }: { delta: { x: number; y: number } }) {
  if (initialDayWidth.value === undefined)
    throw new Error('initialDayWidth is undefined')
  if (minDayWidth.value === undefined)
    throw new Error('minDayWidth is undefined')

  intermediateDayWidth.value =
    initialDayWidth.value + delta.x / (props.index + 1)

  if (intermediateDayWidth.value < minDayWidth.value!) {
    intermediateDayWidth.value = minDayWidth.value
  }

  if (intermediateDayWidth.value > maxDayWidth.value!) {
    intermediateDayWidth.value = maxDayWidth.value
  }

  window.requestAnimationFrame(() => {
    if (!isDragging.value) return

    document.documentElement.style.setProperty(
      `--dayWidth`,
      `${intermediateDayWidth.value}px`
    )
  })
}

function onDragEnd() {
  isDragging.value = false

  store.commit('cssVar/set', {
    name: 'dayWidth',
    value: intermediateDayWidth.value,
  })

  initialDayWidth.value = undefined
  minDayWidth.value = undefined
  maxDayWidth.value = undefined
  intermediateDayWidth.value = undefined

  emit('dragend')
}
</script>

<style lang="scss">
.PmDayWidthController {
  $block: &;
}
</style>
